/**
 * -----------------------------------------------------------------------------
 * User Edit Form
 * -----------------------------------------------------------------------------
 * These components are used to render the form for editing the user details.
 */

import React, { FC } from "react";

import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useCreateModifyOrganizationRoleACLMutation } from "../../../api/network/adminApiServices";
import { ListingResponse } from "../../../api/types/Listing";
import { LegacyUserEntityListingPayload, TLegacyUser, TLegacyUserWithAccount } from "../../../api/types/User";
import { TLegacyUserRoleACL } from "../../../api/types/UserRole";
import {
  AdminFormFieldDropdown,
  AdminFormFieldInput,
  AdminFormFieldRadio,
  AdminFormFieldSubmit,
  TSelectboxOption
} from "../../../components/admin/formFields";
import { useLoggedInUserData } from "../../../utils/user";
import AdminTable from "../../../components/adminTable";
import Pagination, { TPaginationSelected } from "../../../components/pagination";
import { ROLE_ACCESS_LEVELS, ROLE_ACCESS_LEVELS_OPTIONS } from "./constants";
import { roleACLDefinitions } from "./constants-acl-defs";
import svgIcons from "../../../assets/svg/svgIcons";
import { useDispatch } from "react-redux";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import { useSelector } from "react-redux";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { useLoginAsUser } from "../../../hooks/loginAsUser";

interface RoleFormProps {
  values: any;
  errors: any;
  touched: any;
  handleChange: (event: React.ChangeEvent<any>) => void;
  formikSetValue: (field: string, value: any, shouldValidate?: boolean) => void;
  handleBlur: (event: React.FocusEvent<any>) => void;
  formikSetTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
  userCanEdit: boolean;
  loadingData: boolean;
}

export const RoleDetailForm: FC<RoleFormProps> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
}) => {
  const { t } = useTranslation("translation", { keyPrefix: "admins.roles.detailsPage.form" });
  return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
      <AdminFormFieldInput
        customWrapperClass="col-span-4"
        label={t('role_title')}
        type="text"
        id="role_id"
        name="role_id"
        value={values.role_id}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched.role_id}
        error={errors.role_id}
        disabled={!userCanEdit}
        readOnly={true}
        detailsFormField={true}
      />

      <AdminFormFieldInput
        customWrapperClass="col-span-8"
        label={t('description')}
        type="text"
        id="description"
        name="description"
        value={values.description}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched.description}
        error={errors.description}
        disabled={!userCanEdit}
        detailsFormField={true}
      />
    </div>
  );
};

/* not in use, instead we use `RoleAccessControlForm` component in `access-control-form.tsx`
   -----------------------------------------------------------------------------------------
export const RoleAccessControlForm: FC<RoleFormProps> = ({
  values,
  errors,
  touched,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
}) => {
  const { t } = useTranslation("translation", { keyPrefix: "admins.roles.detailsPage" });
  const { t: tMain } = useTranslation("translation"); // , { keyPrefix: "PrivateXML" });

  // prepare group query params
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId")

  const [createModifyRoleACLApiTrigger, { isLoading: isLoadingCreateModifyRoleACL }] = useCreateModifyOrganizationRoleACLMutation();

  return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4 h-96 overflow-y-auto">
      {Object.keys(roleACLDefinitions)?.map((roleAclId: string, i: number) => {
        const roleAclDefItem = roleACLDefinitions[roleAclId as keyof typeof roleACLDefinitions];
        const savedRoleAclItem = values.role_acls.find((item: TLegacyUserRoleACL) => item.aclid.toLowerCase() === roleAclId.toLowerCase());
        const translatedLabel = roleAclDefItem?.i18n ? tMain(roleAclDefItem?.i18n) : roleAclId;
        const possibleValues = roleAclDefItem.values.map((item: number) => `${item}`);
        // const aclDefObject = getParameterCaseInsensitive(roleACLDefinitions, roleAclItem?.aclid);
        return (
          <div className="flex row-between col-span-12">
            <label>{translatedLabel}</label>
            {
              ROLE_ACCESS_LEVELS_OPTIONS.ALL.filter((item: TSelectboxOption) => possibleValues.includes(item.value)).map((item: TSelectboxOption) => {
                return (
                  <AdminFormFieldRadio
                    key={`${translatedLabel}-${item.value}`}
                    label={item.label}
                    name={item.label}
                    id={item.label}
                    type="radio"
                    value={item.value}
                    checked={item.value === `${savedRoleAclItem?.accesslevel}`}
                    onChange={(e) => {
                      if (e.target.value) {
                        const modifyACLObject = {
                          // mode: savedRoleAclItem ? "modify" : "create" as "modify" | "create",
                          accountid: `${thisUserOrganizationId}`,
                          roleid: values.role_id,
                          aclid: roleAclId,
                          accesslevel: parseInt(e.target.value) as 0 | 1 | 2 | 3,
                        }
                        const updateIndex = values.role_acls.findIndex((item: TLegacyUserRoleACL) => item.aclid === roleAclId);
                        let role_acls = [...values.role_acls];
                        if (updateIndex > -1) {
                          role_acls[updateIndex] = modifyACLObject;
                        } else {
                          role_acls.push(modifyACLObject);
                        }
                        formikSetValue("role_acls", role_acls);
                      }
                    }}
                    customWrapperClass="col-span-2 flex items-center bg-white py-2 pl-3 pr-1 rounded-xl"
                    customLabelTextClass="ml-2 text-sm font-bold"
                  />
                )
              })
            }
          </div>
        )
      })
      }
    </div>
  );
}; */

type RoleUsersFormProps = RoleFormProps & {
  roleUsersQueryParam: LegacyUserEntityListingPayload;
  setRoleUsersQueryParam: (value: LegacyUserEntityListingPayload) => void;
  dataRoleUsers: ListingResponse<TLegacyUserWithAccount[]> | undefined;
};

export const RoleUsersForm: FC<RoleUsersFormProps> = ({
  roleUsersQueryParam,
  setRoleUsersQueryParam,
  dataRoleUsers,
  values,
  errors,
  touched,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
}) => {
  const { t } = useTranslation("translation", { keyPrefix: "admins.roles.detailsPage" });
  const { t: tUsers } = useTranslation("translation", { keyPrefix: "admins.users" });

  const modalsState = useSelector((state: { commonReducer: { modals: TModalsState } }) => state.commonReducer.modals);
  const dispatch = useDispatch();

  const { getUserACLAccessLevel } = useACL();
  const { canLoginAs, canLoginAsUser, loginAs } = useLoginAsUser();

  const all_columns = [
    "Name",
    "Email",
    ...(canLoginAs ? ["Action"] : []),
  ];
  const { count, results } = dataRoleUsers || {};

  const AddUsersButton = () => (
    <AdminFormFieldSubmit
      customWrapperClass="col-span-12"
      label={<span className="flex justify-center whitespace-nowrap">
        {t('add_users')}
        <img
          className="ml-2 group-hover:grayscale-0 group-hover:brightness-0 group-hover:invert"
          src={svgIcons.ButtonIconPlus}
          alt={t('add_users')}
        />
      </span>}
      type="button"
      variant="primary"
      onClick={() => {
        dispatch(setModalsData({ ...modalsState, showAddUsersToRoleModal: true }));
      }}
    />
  )

  return (
    <div className="bg-gray-100 pb-4 grid grid-cols-12 gap-4">{/* px-5 pt-4 pb-8 bg-gray-100 */}
      {values.users.length > 0 ? (
        <>
          <AdminTable
            customWrapperClass={`overflow-auto relative col-span-12`}
            // viewportHeightOffset={'400'}
            columns={all_columns}
            data={values.users.map((item: TLegacyUser) => {
              return {
                cellData: [
                  item.userid,
                  item.contactemail,
                  ...(canLoginAs
                    ? [
                        canLoginAsUser(item.userid, item.isactive) ? (
                          <AdminFormFieldSubmit
                            label={tUsers("login_as_button")}
                            variant="primary"
                            onClick={(e) => loginAs(e, item.userid)}
                          />
                        ) : (
                          ""
                        ),
                      ]
                    : []),
                ]
              }
            })}
            isLoading={loadingData}
          />
          {!loadingData && (
            <div className="col-span-12 px-4 -mt-7">
              <Pagination
                pageSize={roleUsersQueryParam.page_size}
                handlePageSizeChange={(e: TSelectboxOption | null) => {
                  // dispatch(setListingQueryParams({ ...listingQueryParams, users: { ...roleUsersQueryParam, page: 1, page_size: parseInt(`${e?.value}`) }}));
                  setRoleUsersQueryParam({ ...roleUsersQueryParam, page: 1, page_size: parseInt(`${e?.value}`) });
                }}
                totalPages={count ? Math.ceil(count / roleUsersQueryParam.page_size) : 1}
                forcePage={roleUsersQueryParam.page - 1}
                handlePageClick={(data: TPaginationSelected) => {
                  // dispatch(setListingQueryParams({ ...listingQueryParams, users: { ...roleUsersQueryParam, page: data?.selected + 1 }}));
                  setRoleUsersQueryParam({ ...roleUsersQueryParam, page: data?.selected + 1 });
                }}
                paginCurrentPage={roleUsersQueryParam.page}
                paginItemsOnCurrentPage={results?.length || 0}
                paginTotalItemsCount={count}
              />
            </div>
          )}
          {getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_ROLE_ASSIGNTOUSER) >= ROLE_ACCESS_LEVELS.WRITE && (
            <div className="col-span-12 flex justify-center px-4 mt-2 mb-2">
              <AddUsersButton />
            </div>
          )}
        </>
      ) : (
        <div className="col-span-12 flex flex-col justify-center text-center py-12 text-lg text-gray-400 font-bold">
          {t('no_users_found')}
          {getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_ROLE_ASSIGNTOUSER) >= ROLE_ACCESS_LEVELS.WRITE && (
            <div className="flex justify-center mt-2">
              <AddUsersButton />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
